import { GeneralDataFacade } from '@/_store/general/general.facade';
import { OffersFacade } from '@/_store/offers/offers.facade';
import { BaseFilter, DEFAULT_PAGINATION_LIMIT, DEFAULT_PAGINATION_OFFSET } from '@/_store/offers/offers.types';
import { DropdownComponent } from '@/common/dropdown/dropdown.component';
import { MobileDialogService } from '@/common/mobile-dialog/mobile-dialog.service';
import { ITopSearchConfig } from '@/new-top-search/top-search.config';
import {
	TopSearchDropdownItemMode,
	TopSearchFilterElType,
} from '@/new-top-search/top-search-dropdown-item/top-search-dropdown-item.types';
import { TopSearchDropdownItemComponent } from '@/new-top-search/top-search-dropdown-item/top-search-dropdown-item.component';
import { Directive, Input, OnDestroy, OnInit, ViewChild, inject } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, Subject, combineLatest, filter, of, take, takeUntil } from 'rxjs';
import { debounceTime, switchMap } from 'rxjs/operators';

const ADVANCED_FETCH_OFFERS_DEBOUNCE_MS = 800;

@Directive()
export abstract class TopSearchFilterItemsBase implements OnInit, OnDestroy {
	@Input({ required: true }) filters: ITopSearchConfig[] = [];
	@Input() itemMaxWidthPx = 400;
	@ViewChild('dropdown') dropdown: DropdownComponent;

	isDropdownOpenArray: boolean[] = [];
	searchBarDropdownOpened = false;

	term$ = new BehaviorSubject<string>('');
	offersBySearchTerm$ = this.term$
		.asObservable()
		.pipe(switchMap((term) => (term.length > 2 ? this.offersFacade.searchOffersBySearchTerm(term) : of(null))));

	isMobile$ = this.generalFacade.isMobile$;

	readonly TopSearchFilterElType = TopSearchFilterElType;
	readonly TopSearchDropdownItemMode = TopSearchDropdownItemMode;

	private readonly offset$ = this.offersFacade.offset$;
	private readonly router = inject(Router);
	/** Trigger used only by advanced filters (vertical variant). */
	private readonly debouncedFetchOffersTrigger$ = new Subject<void>();
	private readonly destroy$ = new Subject<void>();
	private _valueChange = false;

	constructor(
		protected readonly offersFacade: OffersFacade,
		protected readonly generalFacade: GeneralDataFacade,
		protected readonly mobileDialogService: MobileDialogService,
	) {}

	ngOnInit(): void {
		this.isDropdownOpenArray = new Array(this.filters.length).fill(false);

		this.debouncedFetchOffersTrigger$
			.pipe(debounceTime(ADVANCED_FETCH_OFFERS_DEBOUNCE_MS), takeUntil(this.destroy$))
			.subscribe(() => this.fetchOffers());
	}

	ngOnDestroy(): void {
		this.destroy$.next();
		this.destroy$.complete();
	}

	changeOpenState(isOpen: boolean, i: number): void {
		this.isDropdownOpenArray[i] = isOpen;
	}

	set valueChange(value: boolean) {
		this._valueChange = value;
	}

	termUpdated(term: string): void {
		this.term$.next(term);
	}

	chooseProduct(product: BaseFilter): void {
		this.router.navigate(['oferta', product.id]);
	}

	chooseLocation(location: BaseFilter): void {
		this.offersFacade.addLocationsFilterOption(location.id);
		this.goToOfferList();
	}

	fetchOffers(): void {
		this.offersFacade.updateOffset(DEFAULT_PAGINATION_OFFSET);
		combineLatest([this.offersFacade.urlParams$, this.offset$])
			.pipe(
				take(1),
				filter(() => this._valueChange),
			)
			.subscribe(([params, offset]) => {
				this.valueChange = false;
				this.offersFacade.clearOfferList();
				this.offersFacade.fetchOfferList(
					params + `&limit=${DEFAULT_PAGINATION_LIMIT}&offset=${offset}`,
					params + `&limit=${DEFAULT_PAGINATION_LIMIT}&offset=${offset + DEFAULT_PAGINATION_LIMIT}`,
				);
				this.offersFacade.updateQueryParams(params);
			});
	}

	openMobileModal(mode: TopSearchDropdownItemMode): void {
		const title = this.filters.find((config) => config.id === mode)?.text || '';
		const { content: componentFactory } = this.mobileDialogService.openDialogWithComponent<
			TopSearchDropdownItemComponent,
			{ mode: TopSearchDropdownItemMode }
		>(TopSearchDropdownItemComponent, { title, mode });
		const closed$ = new Subject<void>();

		componentFactory.instance.triggerClose.pipe(take(1)).subscribe(() => {
			this.mobileDialogService.close();
		});

		combineLatest([componentFactory.instance.valueChange, this.mobileDialogService.afterClose$])
			.pipe(takeUntil(closed$))
			.subscribe(([valueChange]) => {
				this.valueChange = valueChange;
				this.fetchOffers();
				closed$.next();
				closed$.complete();
			});
	}

	closeDropdown(): void {
		this.dropdown.closeDropdown();
	}

	protected fetchOffersWithDebounce(): void {
		this.debouncedFetchOffersTrigger$.next();
	}

	private goToOfferList(): void {
		this.offersFacade.selected$.pipe(take(1)).subscribe();
	}
}
